/* LightningAlertCard — Phase 62.5 (May 8, 2026)
 *
 * Auto-show top-of-page alert when Weatherbit reports lightning strikes
 * within 15 km in the last hour. Hidden silently otherwise — no clutter
 * on safe days, immediate action on dangerous ones.
 *
 * Hill-stations like Ponmudi, coastal cities during pre-monsoon, and
 * inland convective hot-spots all benefit. This is the most actionable
 * alert any weather app can show.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightning } from "@phosphor-icons/react";
import { getLightningStrikes } from "@/lib/api";

const ALERT_RADIUS_KM = 15;
const ALERT_LOOKBACK_MIN = 60;
const POLL_MS = 4 * 60 * 1000;          // refresh every 4 min while active

function compass(deg) {
  if (deg == null) return null;
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(deg / 45) % 8];
}

function bearing(fromLat, fromLon, toLat, toLon) {
  // Simple initial-bearing computation; only used for cardinal direction.
  const φ1 = (fromLat * Math.PI) / 180;
  const φ2 = (toLat * Math.PI) / 180;
  const Δλ = ((toLon - fromLon) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export default function LightningAlertCard({ location, theme }) {
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!location?.latitude || !location?.longitude) return undefined;
    let cancelled = false;
    let timer;

    const tick = async () => {
      try {
        const r = await getLightningStrikes(
          location.latitude,
          location.longitude,
          ALERT_RADIUS_KM,
          ALERT_LOOKBACK_MIN,
        );
        if (cancelled) return;
        const strikes = r?.strikes || [];
        if (!strikes.length) {
          setAlert(null);
          return;
        }
        // Find closest strike
        const closest = strikes.reduce((a, b) =>
          (a.distance_km ?? Infinity) <= (b.distance_km ?? Infinity) ? a : b,
        );
        const dist = closest.distance_km;
        if (dist == null || dist > ALERT_RADIUS_KM) {
          setAlert(null);
          return;
        }
        const dir =
          closest.lat != null && closest.lon != null
            ? compass(bearing(location.latitude, location.longitude, closest.lat, closest.lon))
            : null;
        setAlert({
          count: strikes.length,
          distance_km: +dist.toFixed(1),
          dir,
        });
      } catch {
        if (!cancelled) setAlert(null);
      }
    };

    tick();
    timer = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [location?.latitude, location?.longitude]);

  return (
    <AnimatePresence>
      {alert && (
        <motion.div
          key="lightning-alert"
          initial={{ opacity: 0, y: -10, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.96 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl px-3.5 py-2.5 flex items-center gap-3 mt-2"
          style={{
            background: "linear-gradient(135deg, rgba(168,85,247,0.16) 0%, rgba(220,38,38,0.10) 100%)",
            border: "1.5px solid rgba(220,38,38,0.45)",
            boxShadow: "0 8px 22px -16px rgba(220,38,38,0.45)",
          }}
          data-testid="lightning-alert"
        >
          <motion.div
            className="shrink-0 rounded-full flex items-center justify-center"
            style={{
              width: 38, height: 38,
              background: "rgba(220,38,38,0.18)",
              border: "1.5px solid rgba(220,38,38,0.55)",
            }}
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <Lightning size={20} weight="fill" color="#dc2626" />
          </motion.div>
          <div className="flex-1 min-w-0">
            <div
              className="text-[9.5px] font-extrabold uppercase tracking-[0.16em]"
              style={{ color: "#dc2626" }}
            >
              Lightning · within 15 km
            </div>
            <div
              className="text-[14px] font-extrabold leading-tight mt-0.5"
              style={{ color: theme.textPrimary }}
              data-testid="lightning-alert-body"
            >
              {alert.distance_km} km {alert.dir ? `${alert.dir} ` : ""}·{" "}
              {alert.count === 1 ? "1 strike" : `${alert.count} strikes`} in the last hour
            </div>
            <div
              className="text-[10.5px] mt-0.5 font-medium leading-snug"
              style={{ color: theme.textSecondary }}
            >
              Move indoors. Stay away from open ground & water.
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
